import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../api/documents';
import type { DocumentItem } from '../types';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface DocumentEditModalProps {
  document: DocumentItem;
  onClose: () => void;
}

export default function DocumentEditModal({ document: doc, onClose }: DocumentEditModalProps) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<string>(doc.notes ?? '');

  const updateMutation = useMutation({
    mutationFn: () => documentsApi.updateDocument(doc.id, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['documents', doc.entity_type, doc.entity_id],
      });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-lg font-medium">Dokument bearbeiten</h3>
            <p className="text-xs text-gray-500 truncate">{doc.filename}</p>
          </div>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Schließen
          </button>
        </div>
        <div className="px-6 py-4 space-y-2">
          <label className="block text-xs font-medium text-gray-600">Notizen</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={INPUT_CLASS}
            rows={4}
            placeholder="Kurze Notiz zu diesem Dokument"
            autoFocus
          />
          {updateMutation.isError && (
            <p className="text-sm text-red-600">Speichern fehlgeschlagen.</p>
          )}
        </div>
        <div className="px-6 py-3 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-800">
            Abbrechen
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Wird gespeichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
